import { useEffect, useState } from "react";
import TabLink from "../../../components/fragment/tab-link/TabLink";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import { ALL_ICONS } from "../../../assets/icons/AllIcons";
import { RazorInputField } from "../../../components/razor-reuse";
import { reactSelectStyleTable } from "../../../utils/Helper";
import TextColorFragment from "../../../components/fragment/text-color/TextColorFragment";
import PercentageChart from "./charts/PercentageChart";
import AverageActivityChart from "./charts/AverageActivityChart";
import NpsTable from "./charts/NpsTable";
import { usePDF } from "react-to-pdf";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/type";
import { systemRoles } from "../../../utils/roles";
import { getMetricChart } from "../../../redux/report";
import { useNavigate } from "react-router-dom";
import { ALL_ROUTES } from "../../../routes/AllRoutes";

export default function ReportIndex() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("NPS Feedback");
  const [filterBy, setfilterBy] = useState({ label: "All", value: "" });

  // Logic to fetch metric
  const { loading_metric, metric_chart, loading_activity } = useSelector(
    (state: RootState) => state.report
  ); // for report
  const { user } = useSelector((state: RootState) => state.auth); // for user permission

  // redirect if user has no permission
  useEffect(() => {
    if (
      !user?.permissions?.includes(systemRoles[14]) &&
      user?.permissions?.length > 0
    ) {
      navigate(ALL_ROUTES.DASHBOARD_OVERVIEW);
    }
  }, [user?.permissions]);

  useEffect(() => {
    const payload = { filter: (filterBy as { value?: string })?.value ?? "" };
    dispatch(getMetricChart(payload) as any);
  }, [filterBy]);

  // To export to pdf
  const { toPDF, targetRef } = usePDF({ filename: "nps-report.pdf" });

  // Tab link list
  const tabLinkList = [
    {
      label: "NPS Feedback",
      active: activeTab === "NPS Feedback",
      onClick: () => {},
    },
  ];

  // Filter By Option
  const filterByOptions = [
    { label: "Last 7 Days", value: "7_days" },
    { label: "30 Days", value: "30_days" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Yearly", value: "yearly" },
    { label: "All", value: "" },
  ];

  return (
    <DashboardLayoutMain
      loading={loading_activity && loading_metric}
      title="Reports"
    >
      <section className="bg-white mx-[2rem] pt-[10px] space-y-4 mt-[15px]">
        <h6 className=" ml-[30px] text-[18px] text-[#252C32] font-semibold">
          All Reports
        </h6>
        <TabLink
          onClick={(param) => {
            setActiveTab(param?.label);
          }}
          list={tabLinkList}
          className="single-customer-tab-link"
        />
      </section>
      <main ref={targetRef} className="px-[2rem] mt-[10px]">
        <div className="flex justify-end items-center gap-[1.6rem]">
          <RazorInputField
            selectOption={filterByOptions}
            type="select"
            color="black-light"
            placeholder="Filter By"
            className=" max-w-[120px]"
            value={filterBy}
            onChange={(e: any) => {
              setfilterBy(e);
            }}
            selectStyles={reactSelectStyleTable}
          />
          <button
            onClick={() => toPDF()}
            className="items-center bg-white cursor-pointer flex min-h-[4.5rem] gap-[0.6rem] px-[1.6rem] py-[0.4rem] rounded-[0.8rem] border-[0.15rem] border-[rgba(60,71,127,0.1)] w-fit "
          >
            <div className="icon-box">
              <figure className="img-box">{ALL_ICONS?.export_down_icon}</figure>
            </div>
            <p className="text">{`Export`}</p>
          </button>
        </div>
        <div className="grid grid-cols-12 gap-[20px] mt-[20px] w-full">
          <div className="bg-white min-h-[130px] p-[16px] col-span-4 rounded-[8px]">
            <div className="flex justify-between items-center gap-x-[4px]">
              <h6 className="font-medium text-[18px] text-[#596080]">
                Promoters(8-10)
              </h6>
            </div>
            <div className="inline-flex items-center gap-[8px] mt-[20px]">
              <p className="text-[12px] text-[#758089]">Total Responses:</p>
              <h6 className="text-[34px] tracking-[0.025rem] font-bold">
                <TextColorFragment
                  color={"rgba(0, 153, 51, 1)"}
                  text={`${metric_chart?.metric_description?.promoters ?? 0}%`}
                />
              </h6>
            </div>
          </div>
          <div className="bg-white min-h-[130px] p-[16px] col-span-4 rounded-[8px]">
            <div className="flex justify-between items-center gap-x-[4px]">
              <h6 className="font-medium text-[18px] text-[#596080]">
                Passives(5-7)
              </h6>
            </div>
            <div className="inline-flex items-center gap-[8px] mt-[20px]">
              <p className="text-[12px] text-[#758089]">Total Responses:</p>
              <h6 className="text-[34px] tracking-[0.025rem] font-bold">
                <TextColorFragment
                  color={"rgba(255, 192, 67, 1)"}
                  text={`${metric_chart?.metric_description?.passives ?? 0}%`}
                />
              </h6>
            </div>
          </div>
          <div className="bg-white min-h-[130px] p-[16px] col-span-4 rounded-[8px]">
            <div className="flex justify-between items-center gap-x-[4px]">
              <h6 className="font-medium text-[18px] text-[#596080]">
                Detractors(0-4)
              </h6>
            </div>
            <div className="inline-flex items-center gap-[8px] mt-[20px]">
              <p className="text-[12px] text-[#758089]">Total Responses:</p>
              <h6 className="text-[34px] tracking-[0.025rem] font-bold">
                <TextColorFragment
                  color={"rgba(200, 16, 46, 1)"}
                  text={`${metric_chart?.metric_description?.detractors ?? 0}%`}
                />
              </h6>
            </div>
          </div>

          {/* pie chart */}
          <PercentageChart />
          <AverageActivityChart />
        </div>
      </main>
      {/* table */}
      <section className="my-[25px] px-[2rem]">
        <NpsTable />
      </section>
    </DashboardLayoutMain>
  );
}
